/**
 * Workspace Store
 *
 * Manages workspace state
 */
import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import type { Workspace, WorkspaceCreate } from '@/types'

class WorkspaceStore {
  workspaces: Workspace[] = []
  currentWorkspace: Workspace | null = null
  isLoading = false
  error: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  async fetchWorkspaces() {
    this.isLoading = true
    this.error = null

    try {
      const response = await apiClient.getWorkspaces()

      runInAction(() => {
        this.workspaces = response.items
        // Set first workspace as current if none selected
        if (!this.currentWorkspace && response.items.length > 0) {
          this.currentWorkspace = response.items[0]
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch workspaces'
        this.isLoading = false
      })
      throw error
    }
  }

  async createWorkspace(data: WorkspaceCreate) {
    this.isLoading = true
    this.error = null

    try {
      const workspace = await apiClient.createWorkspace(data)

      runInAction(() => {
        this.workspaces.push(workspace)
        this.currentWorkspace = workspace
        this.isLoading = false
      })

      return workspace
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to create workspace'
        this.isLoading = false
      })
      throw error
    }
  }

  setCurrentWorkspace(workspace: Workspace) {
    this.currentWorkspace = workspace
  }

  reset() {
    this.workspaces = []
    this.currentWorkspace = null
    this.isLoading = false
    this.error = null
  }
}

export const workspaceStore = new WorkspaceStore()
export default workspaceStore
